import React from "react";
import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import {
  buildBreadcrumbSchema,
  buildPageTitle,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";
import {
  PageWrapper,
  Hero,
  Eyebrow,
  Title,
  LastUpdated,
  Content,
  Introduction,
  Section,
  SectionTitle,
  SubSection,
  SubSectionTitle,
  SectionContent,
  BulletList,
  BulletPoint,
  ImportantNote,
  EmailLink,
  LegalFooter,
} from "../legal/LegalPage.styles";

const TermsPage = () => {
  return (
    <MainContainer>
      <SeoHead
        title={buildPageTitle("Terms of Use")}
        description="Read the Fapify terms of use for AI porn generation, user content rules, account responsibilities, and eligibility requirements."
        path="/terms-and-conditions"
        jsonLd={[
          buildWebsiteSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Terms of Use", path: "/terms-and-conditions" },
          ]),
        ]}
      />
      <PageWrapper>
        <Hero>
          <Eyebrow>Terms of Use</Eyebrow>
          <Title>The rules of the road</Title>
          <LastUpdated>Last updated: 14 July 2025</LastUpdated>
        </Hero>

        <Content>
          <Introduction>
            Welcome to Fapify.com (the "Site", "Service", "we", "us" or "our").
            These Terms of Use (the "Terms") form a legally binding contract
            between you ("you" or the "User") and us. By accessing, browsing or
            using the Service, you confirm that you have read, understood and
            agree to be bound by these Terms and by all applicable laws and
            regulations. If you do not agree, you must not use the Service.
          </Introduction>

          <Section>
            <SectionTitle>1. Service Description</SectionTitle>
            <SectionContent>
              The Service enables Users to upload still images of people and,
              via proprietary artificial-intelligence ("AI") models, generate
              short video clips based on those images (the "AI‑Generated
              Content").
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>2. Eligibility</SectionTitle>
            <SectionContent>
              <BulletPoint>
                You must be at least 18 years old (or the age of majority in
                your jurisdiction, whichever is higher) and have the legal
                capacity to enter into these Terms.
              </BulletPoint>
              <BulletPoint>
                You affirm that you are the person depicted in every image you
                upload; you must not upload images of any other individual for
                AI processing.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>3. User Accounts and Security</SectionTitle>
            <SectionContent>
              <BulletPoint>
                You may need to create an account. You are responsible for
                maintaining the confidentiality of your login credentials and
                for all activity under your account.
              </BulletPoint>
              <BulletPoint>
                You must promptly notify us of any unauthorized use of your
                account.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>4. User Content</SectionTitle>
            <SectionContent>
              "User Content" means all images, data, text, audio, video, and
              other material that you upload, transmit or otherwise make
              available through the Service.
            </SectionContent>

            <SubSection>
              <SubSectionTitle>4.1 Your Representations</SubSectionTitle>
              <SectionContent>
                By uploading User Content you represent and warrant that:
                <BulletList>
                  <BulletPoint>
                    you are the sole subject depicted in the image(s) and you
                    own or otherwise control all rights necessary to submit the
                    User Content;
                  </BulletPoint>
                  <BulletPoint>
                    the User Content does not infringe, misappropriate or
                    violate any intellectual-property, privacy, publicity or
                    other proprietary rights; and
                  </BulletPoint>
                  <BulletPoint>
                    the User Content complies with Section 4.3 (Prohibited
                    Content).
                  </BulletPoint>
                </BulletList>
              </SectionContent>
            </SubSection>

            <SubSection>
              <SubSectionTitle>4.2 Licence to Us</SubSectionTitle>
              <SectionContent>
                You grant us a limited, non-exclusive, royalty-free licence to
                host, store, reproduce, modify, adapt, create derivative works
                (including AI‑Generated Content) and otherwise use the User
                Content solely for the purpose of operating and providing the
                Service to you, as well as for improving our models, security,
                compliance and support.
                <ImportantNote>
                  <strong>Important:</strong> We never share your User Content
                  (uploaded images) or AI‑Generated Content with any third
                  parties. Your content remains private and is only processed by
                  our systems to provide the Service.
                </ImportantNote>
              </SectionContent>
            </SubSection>

            <SubSection>
              <SubSectionTitle>4.3 Prohibited Content</SubSectionTitle>
              <SectionContent>
                You agree that you will not upload, generate or distribute via
                the Service any content that:
                <BulletList>
                  <BulletPoint>
                    depicts minors (anyone under 18) in any sexual context;
                  </BulletPoint>
                  <BulletPoint>
                    depicts any person other than yourself without that person's
                    explicit, verifiable consent;
                  </BulletPoint>
                  <BulletPoint>
                    promotes violence, terrorism, self-harm or hate;
                  </BulletPoint>
                  <BulletPoint>
                    is unlawful, defamatory, fraudulent or otherwise
                    objectionable;
                  </BulletPoint>
                  <BulletPoint>
                    infringes any third-party rights; or
                  </BulletPoint>
                  <BulletPoint>
                    violates any applicable law or regulation.
                  </BulletPoint>
                </BulletList>
                NSFW or sexually explicit content featuring yourself, created
                from your own uploaded images, is permitted provided it complies
                with the above restrictions and all applicable laws. You remain
                fully responsible for such content, including any sharing or
                publication.
              </SectionContent>
            </SubSection>
          </Section>

          <Section>
            <SectionTitle>5. AI‑Generated Content</SectionTitle>
            <SectionContent>
              <BulletPoint>
                AI‑Generated Content is created algorithmically and may contain
                artifacts, inaccuracies or distortions. We do not guarantee that
                AI‑Generated Content will be accurate, realistic, or fit for any
                particular purpose.
              </BulletPoint>
              <BulletPoint>
                You acknowledge that AI‑Generated Content may unintentionally
                produce sensitive or offensive material; you assume all risk
                arising from its use or publication.
              </BulletPoint>
              <BulletPoint>
                Subject to your compliance with these Terms and the payment of
                any applicable fees, we assign to you all of our right, title
                and interest in and to the AI‑Generated Content we specifically
                create for you, excluding any underlying software or models.
              </BulletPoint>
              <BulletPoint>
                <strong>Your Responsibility.</strong> You are solely responsible
                for all AI‑Generated Content created from your uploads,
                including any sharing, publication or other use. You must ensure
                that such content complies with all applicable laws, regulations
                and these Terms.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>6. Intellectual‑Property Rights</SectionTitle>
            <SectionContent>
              <BulletPoint>
                All intellectual-property rights in the Site, underlying models,
                software, trademarks and logos are and shall remain our
                exclusive property or that of our licensors.
              </BulletPoint>
              <BulletPoint>
                Nothing in these Terms grants you any right to use our
                trademarks or service marks without prior written consent.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>7. Fees and Payment</SectionTitle>
            <SectionContent>
              If the Service offers paid tiers, drops or subscriptions, you
              agree to pay all fees in accordance with the pricing and billing
              terms posted on the Site. Fees are non-refundable except as
              required by law or expressly stated otherwise.
            </SectionContent>

            <SubSection>
              <SubSectionTitle>7.1 Refunds Policy</SubSectionTitle>
              <SectionContent>
                We do not provide refunds under any circumstances, including but
                not limited to:
                <BulletList>
                  <BulletPoint>
                    Purchased drops, regardless of whether they have been used
                    or not;
                  </BulletPoint>
                  <BulletPoint>
                    Subscription fees or any other payments made to the Service;
                  </BulletPoint>
                  <BulletPoint>
                    Drops consumed for AI-Generated Content, regardless of your
                    satisfaction with the results, technical issues, or any
                    other reason.
                  </BulletPoint>
                </BulletList>
                Once drops are used to generate content, they are considered
                consumed and will not be restored or refunded. You acknowledge
                that AI-Generated Content may vary in quality and that the
                consumption of drops is final upon generation.
              </SectionContent>
            </SubSection>
          </Section>

          <Section>
            <SectionTitle>8. DMCA / Copyright Complaints</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We respect intellectual-property rights and respond to notices
                of alleged infringement in accordance with the Digital
                Millennium Copyright Act ("DMCA").
              </BulletPoint>
              <BulletPoint>
                For takedown requests please email{" "}
                <EmailLink href="mailto:support@fapify.com">
                  support@fapify.com
                </EmailLink>{" "}
                with the required information.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>9. Disclaimers</SectionTitle>
            <SectionContent>
              THE SERVICE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS
              AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE AND NON-INFRINGEMENT.
              <br />
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE
              OR ERROR-FREE.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>10. Limitation of Liability</SectionTitle>
            <SectionContent>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL OR EXEMPLARY
              DAMAGES (INCLUDING LOSS OF PROFITS, DATA, GOODWILL OR OTHER
              INTANGIBLE LOSSES) ARISING OUT OF OR IN CONNECTION WITH THE
              SERVICE.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>11. Indemnification</SectionTitle>
            <SectionContent>
              You agree to indemnify, defend and hold harmless the Site and its
              affiliates, officers, directors, employees and agents from any
              claims, damages, liabilities, costs and expenses (including
              reasonable attorneys' fees) arising from:
              <BulletList>
                <BulletPoint>(a) your User Content;</BulletPoint>
                <BulletPoint>(b) your use of the Service;</BulletPoint>
                <BulletPoint>(c) your breach of these Terms; or</BulletPoint>
                <BulletPoint>
                  (d) your violation of any law or third-party rights.
                </BulletPoint>
              </BulletList>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>12. Termination</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may suspend or terminate your access to the Service at any
                time, with or without notice, for conduct that we believe
                violates these Terms or is harmful to other Users, us or third
                parties.
              </BulletPoint>
              <BulletPoint>
                Sections 4–13 shall survive any termination.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>13. Modifications</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may revise these Terms from time to time. Material changes
                will be posted on this page and, where appropriate, notified to
                you by email.
              </BulletPoint>
              <BulletPoint>
                By continuing to use the Service after changes take effect, you
                accept the revised Terms.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>
              14. Governing Law and Dispute Resolution
            </SectionTitle>
            <SectionContent>
              These Terms are governed by the laws of Singapore, without regard
              to its conflict-of-laws principles. Any dispute shall be resolved
              through good-faith negotiations; if unresolved, the courts of
              Singapore shall have exclusive jurisdiction.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>
              15. Prohibited Use - Deepfakes and Synthetic Media
            </SectionTitle>
            <SectionContent>
              The User shall not use, and shall not permit any third party to
              use, the Service for the purpose of creating, generating,
              modifying, or distributing any synthetic, manipulated, or
              “deepfake” content depicting any identifiable individual, where
              such content is intended for subsequent publication,
              dissemination, or public display, unless the User has obtained the
              prior, explicit, and legally valid consent of each individual
              whose image, likeness, voice, or other personal attributes are
              used or simulated. The User represents and warrants that all such
              consents comply with applicable laws and regulations and shall be
              solely responsible for any claims, damages, liabilities, or losses
              arising from a failure to obtain or maintain such consent.
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle>16. Contact</SectionTitle>
            <SectionContent>
              Questions about these Terms may be directed to{" "}
              <EmailLink href="mailto:support@fapify.com">
                support@fapify.com
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <LegalFooter>
            © 2025 Fapify.com All rights reserved.
            <br />
            Terms of Service of Fapify - NSFW AI Video Generator
          </LegalFooter>
        </Content>
      </PageWrapper>
    </MainContainer>
  );
};

export default TermsPage;
